import { ReactNode, useContext, useMemo } from "react"
import { CHAIN_ID } from "@mezo/sdk"
import { Button, ButtonProps } from "@mezo-org/mezo-clay"

import {
  selectIsConnected,
  selectIsOnMezoChain,
  useDappSelector,
} from "#/redux-state"
import { IS_MEZO_MAINNET, MEZO_NETWORK } from "#/shared/constants"
import { FlowContext } from "#/shared/context"

import ConnectWalletButton from "./ConnectWalletButton"
import SwitchChainButton from "./SwitchChainButton"

export type MatsnetActionButtonProps = {
  onClick: () => unknown
  isLoading?: boolean
  isDisabled?: boolean
  dataAttr?: string
  size?: ButtonProps["size"]
  shape?: ButtonProps["shape"]
  kind?: ButtonProps["kind"]
  baseButtonOverrides?: Record<string, string>
  children: ReactNode
}

export default function MatsnetActionButton({
  onClick,
  isLoading,
  isDisabled,
  dataAttr,
  size = "medium",
  shape = "default",
  kind = "primary",
  baseButtonOverrides,
  children,
}: MatsnetActionButtonProps) {
  const isOnMezoChain = useDappSelector(selectIsOnMezoChain)
  const isConnected = useDappSelector(selectIsConnected)

  const { isBitcoinFlow } = useContext(FlowContext)

  const hasCorrectChain = useMemo(
    () => isBitcoinFlow || isOnMezoChain,
    [isBitcoinFlow, isOnMezoChain],
  )

  const buttonOverrides = {
    BaseButton: {
      style: () => ({
        ...baseButtonOverrides,
      }),
    },
  }

  if (!isConnected) {
    return (
      <ConnectWalletButton
        width="100%"
        overrides={buttonOverrides}
        size={size}
        shape={shape}
      >
        Sign in
      </ConnectWalletButton>
    )
  }

  if (!hasCorrectChain) {
    return (
      <SwitchChainButton
        width="100%"
        label={IS_MEZO_MAINNET ? "Mezo" : "Mezo Testnet"}
        chainId={CHAIN_ID[MEZO_NETWORK]}
        size={size}
        shape={shape}
        overrides={buttonOverrides}
      />
    )
  }

  return (
    <Button
      width="100%"
      kind={kind}
      size={size}
      shape={shape}
      onClick={onClick}
      isLoading={isLoading}
      disabled={isDisabled}
      data-attr={dataAttr}
      overrides={buttonOverrides}
    >
      {children}
    </Button>
  )
}
